<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Kunskapsbas</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Lägg till information som dina AI-agenter kan använda för att svara på frågor
        </p>
      </div>
      <button
        @click="openAddModal"
        class="mt-4 sm:mt-0 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center"
      >
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
        </svg>
        Lägg till kunskap
      </button>
    </div>

    <!-- Filter by Agent -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      <div class="flex flex-col sm:flex-row sm:items-center gap-4">
        <label class="text-sm font-medium text-gray-700 dark:text-gray-300">
          Filtrera per agent:
        </label>
        <select
          v-model="filterAgentId"
          @change="fetchKnowledge"
          class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        >
          <option :value="null">Alla kunskapsbaser</option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
        <span class="text-sm text-gray-500 dark:text-gray-400">
          {{ knowledge.length }} kunskapsbas{{ knowledge.length !== 1 ? 'er' : '' }}
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>

    <!-- Knowledge List -->
    <div v-else-if="knowledge.length > 0" class="space-y-4">
      <div
        v-for="item in knowledge"
        :key="item.knowledge_id"
        class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <div class="flex items-center flex-wrap gap-2">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ item.title }}</h3>
              <span
                v-if="item.category"
                class="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
              >
                {{ item.category }}
              </span>
              <span
                v-if="!item.is_active"
                class="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
              >
                Inaktiv
              </span>
            </div>
            <p class="mt-2 text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{{ item.content }}</p>
            
            <!-- Assigned Agents -->
            <div class="mt-3 flex flex-wrap gap-2">
              <span class="text-xs text-gray-500 dark:text-gray-400">Tilldelad till:</span>
              <span
                v-for="agentId in item.assigned_agent_ids"
                :key="agentId"
                class="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
              >
                {{ getAgentName(agentId) }}
              </span>
              <span
                v-if="!item.assigned_agent_ids || item.assigned_agent_ids.length === 0"
                class="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
              >
                Ingen agent tilldelad
              </span>
            </div>
            
            <div class="mt-3 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
              <span>Använd {{ item.usage_count }} gånger</span>
              <span>•</span>
              <span>Skapad {{ formatDate(item.created_at) }}</span>
            </div>
          </div>
          <div class="flex items-center space-x-2 ml-4">
            <button
              @click="editItem(item)"
              class="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
              title="Redigera"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </button>
            <button
              @click="deleteItem(item)"
              class="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
              title="Ta bort"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
      </svg>
      <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">Ingen kunskap ännu</h3>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
        Lägg till information som dina AI-agenter kan använda för att ge bättre svar.
      </p>
      <button
        @click="openAddModal"
        class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
      >
        Lägg till första kunskapen
      </button>
    </div>

    <!-- Add/Edit Modal -->
    <Teleport to="body">
      <div
        v-if="showAddModal"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="closeModal"
      >
        <div class="flex items-center justify-center min-h-screen px-4">
          <div class="fixed inset-0 bg-black bg-opacity-50"></div>
          <div class="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full p-6">
            <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {{ editingItem ? 'Redigera kunskap' : 'Lägg till kunskap' }}
            </h2>
            
            <form @submit.prevent="saveItem" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Titel *
                </label>
                <input
                  v-model="form.title"
                  type="text"
                  required
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="T.ex. Öppettider"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Innehåll *
                </label>
                <textarea
                  v-model="form.content"
                  required
                  rows="4"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Beskriv informationen som AI:n ska kunna använda..."
                ></textarea>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kategori
                </label>
                <input
                  v-model="form.category"
                  type="text"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="T.ex. Allmänt, Priser, Kontakt"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nyckelord (kommaseparerade)
                </label>
                <input
                  v-model="form.keywords"
                  type="text"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="T.ex. öppettider, tider, öppet"
                />
              </div>

              <!-- Agent Selection -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tilldela till agenter *
                </label>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Välj vilka AI-agenter som ska ha tillgång till denna kunskap
                </p>
                <div class="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                  <label
                    v-for="agent in agents"
                    :key="agent.id"
                    class="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg"
                  >
                    <input
                      type="checkbox"
                      :value="agent.id"
                      v-model="form.agentIds"
                      class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div class="flex-1">
                      <span class="text-sm font-medium text-gray-900 dark:text-white">{{ agent.name }}</span>
                      <p v-if="agent.description" class="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {{ agent.description }}
                      </p>
                    </div>
                  </label>
                  <p v-if="agents.length === 0" class="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                    Inga agenter hittades. Skapa en agent först.
                  </p>
                </div>
                <p v-if="form.agentIds.length === 0" class="text-xs text-red-500 mt-1">
                  Välj minst en agent
                </p>
              </div>
              
              <div class="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  @click="closeModal"
                  class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  :disabled="saving || form.agentIds.length === 0"
                  class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ saving ? 'Sparar...' : (editingItem ? 'Uppdatera' : 'Lägg till') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { knowledgeService, type KnowledgeEntry } from '../services/knowledge'
import { agentsService } from '../services/agents'

interface Agent {
  id: number
  name: string
  description?: string
}

const loading = ref(true)
const saving = ref(false)
const showAddModal = ref(false)
const knowledge = ref<KnowledgeEntry[]>([])
const editingItem = ref<KnowledgeEntry | null>(null)
const agents = ref<Agent[]>([])
const filterAgentId = ref<number | null>(null)

const form = ref({
  title: '',
  content: '',
  category: '',
  keywords: '',
  agentIds: [] as number[]
})

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('sv-SE')
}

const getAgentName = (agentId: number): string => {
  const agent = agents.value.find(a => a.id === agentId)
  return agent?.name || `Agent ${agentId}`
}

const fetchKnowledge = async () => {
  loading.value = true
  try {
    const result = await knowledgeService.getKnowledge(filterAgentId.value || undefined)
    if (result.success && result.data) {
      knowledge.value = result.data
    }
  } catch (error) {
    console.error('Failed to fetch knowledge:', error)
  } finally {
    loading.value = false
  }
}

const fetchAgents = async () => {
  try {
    const result = await agentsService.getAgents()
    if (Array.isArray(result) && result.length > 0) {
      agents.value = result.map((a: any) => ({
        id: Number(a.id),
        name: a.name,
        description: a.description
      }))
    }
  } catch (error) {
    console.error('Failed to fetch agents:', error)
  }
}

const openAddModal = () => {
  editingItem.value = null
  form.value = {
    title: '',
    content: '',
    category: '',
    keywords: '',
    agentIds: agents.value.length > 0 ? [agents.value[0].id] : []
  }
  showAddModal.value = true
}

const editItem = (item: KnowledgeEntry) => {
  editingItem.value = item
  form.value = {
    title: item.title,
    content: item.content,
    category: item.category || '',
    keywords: item.keywords || '',
    agentIds: item.assigned_agent_ids || [item.agent_id]
  }
  showAddModal.value = true
}

const deleteItem = async (item: KnowledgeEntry) => {
  if (!confirm('Är du säker på att du vill ta bort denna kunskap?')) return
  
  try {
    const result = await knowledgeService.deleteKnowledge(item.knowledge_id)
    if (result.success) {
      knowledge.value = knowledge.value.filter(k => k.knowledge_id !== item.knowledge_id)
    }
  } catch (error) {
    console.error('Failed to delete knowledge:', error)
  }
}

const saveItem = async () => {
  if (form.value.agentIds.length === 0) return
  
  saving.value = true
  try {
    if (editingItem.value) {
      // Update
      const result = await knowledgeService.updateKnowledge(editingItem.value.knowledge_id, {
        title: form.value.title,
        content: form.value.content,
        category: form.value.category || undefined,
        keywords: form.value.keywords || undefined,
        agentIds: form.value.agentIds
      })
      if (result.success) {
        await fetchKnowledge()
        closeModal()
      }
    } else {
      // Create
      const result = await knowledgeService.addKnowledge({
        agentIds: form.value.agentIds,
        title: form.value.title,
        content: form.value.content,
        category: form.value.category || undefined,
        keywords: form.value.keywords || undefined
      })
      if (result.success) {
        await fetchKnowledge()
        closeModal()
      }
    }
  } catch (error) {
    console.error('Failed to save knowledge:', error)
  } finally {
    saving.value = false
  }
}

const closeModal = () => {
  showAddModal.value = false
  editingItem.value = null
  form.value = {
    title: '',
    content: '',
    category: '',
    keywords: '',
    agentIds: agents.value.length > 0 ? [agents.value[0].id] : []
  }
}

onMounted(() => {
  fetchAgents()
  fetchKnowledge()
})
</script>
